import { AlertTriangleIcon, InfoIcon, OctagonAlertIcon } from 'lucide-react'
import { tv } from 'tailwind-variants'

import { OUTAGE_BANNER_CONFIG } from '@/config'

type OutageSeverity = 'info' | 'warning' | 'critical'

const OUTAGE_SEVERITIES: Record<string, OutageSeverity> = {
  INFO: 'info',
  WARNING: 'warning',
  CRITICAL: 'critical',
} as const

const parseSeverity = (raw: string | undefined): OutageSeverity =>
  Object.values(OUTAGE_SEVERITIES).includes(raw as OutageSeverity)
    ? (raw as OutageSeverity)
    : 'warning'

const banner = tv({
  slots: {
    container:
      'shrink-0 sticky top-0 z-50 flex items-start gap-3 border-b px-6 py-3',
    icon: 'h-5 w-5 shrink-0 mt-0.5',
    title: 'text-sm font-medium',
    message: 'text-sm',
  },
  variants: {
    severity: {
      [OUTAGE_SEVERITIES.INFO]: {
        container: 'border-blue-300 bg-blue-50',
        icon: 'text-blue-600',
        title: 'text-blue-900',
        message: 'text-blue-800',
      },
      [OUTAGE_SEVERITIES.WARNING]: {
        container: 'border-amber-300 bg-amber-50',
        icon: 'text-amber-600',
        title: 'text-amber-900',
        message: 'text-amber-800',
      },
      [OUTAGE_SEVERITIES.CRITICAL]: {
        container: 'border-red-300 bg-red-50',
        icon: 'text-red-600',
        title: 'text-red-900',
        message: 'text-red-800',
      },
    },
  },
})

const SEVERITY_DEFAULTS = {
  [OUTAGE_SEVERITIES.INFO]: { title: 'Notice', icon: InfoIcon },
  [OUTAGE_SEVERITIES.WARNING]: {
    title: 'Service disruption',
    icon: AlertTriangleIcon,
  },
  [OUTAGE_SEVERITIES.CRITICAL]: {
    title: 'Critical outage',
    icon: OctagonAlertIcon,
  },
}

const DEFAULT_MESSAGE =
  'We are currently experiencing an outage due to an upstream provider. We are looking into the issue and will be back and operational soon.'

export const OutageBanner = () => {
  const { bannerEnabled, bannerMessage, bannerSeverity } = OUTAGE_BANNER_CONFIG

  const severity = parseSeverity(bannerSeverity)

  if (!bannerEnabled) return null

  const { title, icon: Icon } = SEVERITY_DEFAULTS[severity]

  const message = bannerMessage || DEFAULT_MESSAGE

  const styles = banner({
    severity: severity,
  })

  return (
    <output className={styles.container()} aria-live="polite">
      <Icon className={styles.icon()} aria-hidden="true" />
      <div>
        <p className={styles.title()}>{title}</p>
        <p className={styles.message()}>{message}</p>
      </div>
    </output>
  )
}
